/**
 * PawRinse Landing Page Interactive Logic - Refined Edition
 * Handles dynamic variant selection, percentage savings calculation,
 * mobile sticky CTA observer, loading states, and accessible FAQ.
 */

(function () {
  'use strict';

  function initPawRinseLanding() {
    const root = document.querySelector('.pawrins-landing');
    if (!root) return;

    // --- 1. Variant Selection & Reactive Pricing ---
    const productJsonEl = root.querySelector('.pawrins-product-json') || document.getElementById('PawRinse-ProductJson');
    let productData = null;
    if (productJsonEl) {
      try {
        productData = JSON.parse(productJsonEl.textContent);
      } catch (e) {
        console.warn('[PawRinse] Error parsing product JSON:', e);
      }
    }

    const form = root.querySelector('form.pawrins-product-form') || root.querySelector('form[action*="/cart/add"]');
    const variantInput = form ? (form.querySelector('input.pawrins-variant-id') || form.querySelector('input[name="id"]')) : null;
    const heroImgEl = root.querySelector('#PawRinseHeroImage');
    const priceEl = root.querySelector('.pr-current-price');
    const comparePriceEl = root.querySelector('.pr-compare-price');
    const saveBadgeEl = root.querySelector('.pr-badge-save');
    const savePercentEl = root.querySelector('.pr-save-percent');
    const atcBtn = form ? (form.querySelector('.pawrins-add-to-cart') || form.querySelector('.pr-btn-primary')) : null;
    const stickyBar = root.querySelector('.pr-sticky-bottom-bar');
    const stickyAtcBtn = root.querySelector('.pr-sticky-btn');
    const stickyPriceEl = root.querySelector('.pr-sticky-price');
    const finalCtaPriceEl = root.querySelector('.pr-final-price-text');
    const secondaryPriceEl = root.querySelector('.pr-secondary-price-text');

    const selectedOptions = {};

    function formatMoney(cents) {
      if (window.Shopify && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      return '£' + (cents / 100).toFixed(2);
    }

    function updateVariantState() {
      if (!productData || !productData.variants || productData.variants.length === 0) return;

      // Find matched variant based on selectedOptions
      let matchedVariant = null;

      if (productData.variants.length === 1) {
        matchedVariant = productData.variants[0];
      } else {
        matchedVariant = productData.variants.find((variant) => {
          if (variant.options && Array.isArray(variant.options)) {
            return variant.options.every((optVal, index) => {
              const optIndex = index + 1;
              const selectedVal = selectedOptions['option' + optIndex];
              return !selectedVal || selectedVal === optVal;
            });
          }
          return (
            (!selectedOptions['option1'] || variant.option1 === selectedOptions['option1']) &&
            (!selectedOptions['option2'] || variant.option2 === selectedOptions['option2']) &&
            (!selectedOptions['option3'] || variant.option3 === selectedOptions['option3'])
          );
        });
      }

      if (!matchedVariant) {
        matchedVariant = productData.variants[0];
      }

      if (!matchedVariant) return;

      // 1. Update hidden variant input
      if (variantInput) {
        variantInput.value = matchedVariant.id;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // 2. Update prices
      const formattedPrice = formatMoney(matchedVariant.price);
      if (priceEl) priceEl.textContent = formattedPrice;
      if (stickyPriceEl) stickyPriceEl.textContent = formattedPrice;
      if (finalCtaPriceEl) finalCtaPriceEl.textContent = formattedPrice;
      if (secondaryPriceEl) secondaryPriceEl.textContent = formattedPrice;

      // 3. Update dynamic percentage discount badge
      if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
        const formattedCompare = formatMoney(matchedVariant.compare_at_price);
        const discountPct = Math.round(
          ((matchedVariant.compare_at_price - matchedVariant.price) / matchedVariant.compare_at_price) * 100
        );

        if (comparePriceEl) {
          comparePriceEl.textContent = formattedCompare;
          comparePriceEl.style.display = 'inline';
        }
        if (saveBadgeEl) {
          saveBadgeEl.style.display = 'inline-flex';
          if (savePercentEl) {
            savePercentEl.textContent = discountPct + '%';
          }
        }
      } else {
        if (comparePriceEl) comparePriceEl.style.display = 'none';
        if (saveBadgeEl) saveBadgeEl.style.display = 'none';
      }

      // 4. Update variant image if provided
      if (matchedVariant.featured_image && heroImgEl) {
        heroImgEl.src = matchedVariant.featured_image.src;
      }

      // 5. Update availability & CTA button
      if (atcBtn) {
        const atcTextEl = atcBtn.querySelector('.pr-btn-text') || atcBtn.querySelector('span');
        if (matchedVariant.available) {
          atcBtn.removeAttribute('disabled');
          atcBtn.disabled = false;
          atcBtn.classList.remove('is-sold-out');
          if (atcTextEl) {
            atcTextEl.textContent = 'Add to cart — ' + formattedPrice;
          }
        } else {
          atcBtn.setAttribute('disabled', 'disabled');
          atcBtn.disabled = true;
          atcBtn.classList.add('is-sold-out');
          if (atcTextEl) {
            atcTextEl.textContent = 'Sold out';
          }
        }
      }

      if (stickyAtcBtn) {
        const stickyTextSpan = stickyAtcBtn.querySelector('span');
        if (matchedVariant.available) {
          stickyAtcBtn.removeAttribute('disabled');
          stickyAtcBtn.disabled = false;
          if (stickyTextSpan) stickyTextSpan.textContent = 'Add to cart';
        } else {
          stickyAtcBtn.setAttribute('disabled', 'disabled');
          stickyAtcBtn.disabled = true;
          if (stickyTextSpan) stickyTextSpan.textContent = 'Sold out';
        }
      }
    }

    // Initialize variant pill click listeners
    const optionContainers = root.querySelectorAll('.pr-variant-option');
    optionContainers.forEach((container) => {
      const optionIndex = container.dataset.optionIndex;
      const selectedValueLabel = container.querySelector('.pr-option-selected-val');
      const pills = container.querySelectorAll('.pr-pill-btn');

      pills.forEach((pill) => {
        if (pill.classList.contains('is-active')) {
          selectedOptions['option' + optionIndex] = pill.dataset.value;
          pill.setAttribute('aria-checked', 'true');
        } else {
          pill.setAttribute('aria-checked', 'false');
        }

        pill.addEventListener('click', (e) => {
          e.preventDefault();
          if (pill.getAttribute('aria-disabled') === 'true' || pill.disabled) return;

          pills.forEach((p) => {
            p.classList.remove('is-active');
            p.setAttribute('aria-checked', 'false');
          });

          pill.classList.add('is-active');
          pill.setAttribute('aria-checked', 'true');

          const val = pill.dataset.value;
          selectedOptions['option' + optionIndex] = val;
          if (selectedValueLabel) selectedValueLabel.textContent = val;

          updateVariantState();
        });
      });
    });

    // --- 2. Add to Cart Form Loading State & Native Submission ---
    if (form) {
      let isSubmitting = false;
      form.addEventListener('submit', (e) => {
        if (atcBtn && atcBtn.disabled) {
          e.preventDefault();
          return;
        }
        if (isSubmitting) {
          e.preventDefault();
          return;
        }
        isSubmitting = true;
        if (atcBtn) {
          atcBtn.classList.add('is-loading');
          atcBtn.setAttribute('aria-busy', 'true');
        }

        // Allow reset after 3s if navigation is delayed
        setTimeout(() => {
          isSubmitting = false;
          if (atcBtn) {
            atcBtn.classList.remove('is-loading');
            atcBtn.removeAttribute('aria-busy');
          }
        }, 3000);
      });
    }

    // --- 3. Mobile Sticky Bottom Bar (Tracks main CTA visibility) ---
    if (stickyBar && atcBtn) {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Show sticky bar ONLY when main CTA button is out of the screen
              if (!entry.isIntersecting) {
                stickyBar.classList.add('is-visible');
              } else {
                stickyBar.classList.remove('is-visible');
              }
            });
          },
          { rootMargin: '-10px 0px 0px 0px', threshold: 0.1 }
        );
        observer.observe(atcBtn);
      } else {
        window.addEventListener('scroll', () => {
          const btnRect = atcBtn.getBoundingClientRect();
          if (btnRect.bottom < 0 || btnRect.top > window.innerHeight) {
            stickyBar.classList.add('is-visible');
          } else {
            stickyBar.classList.remove('is-visible');
          }
        }, { passive: true });
      }

      if (stickyAtcBtn) {
        stickyAtcBtn.addEventListener('click', (e) => {
          e.preventDefault();
          if (atcBtn && !atcBtn.disabled) {
            atcBtn.click();
          }
        });
      }
    }

    // --- 4. Secondary CTA (Infographic) & Final CTA Smooth Scroll ---
    const secondaryCtaBtn = root.querySelector('.pr-secondary-buy-trigger');
    if (secondaryCtaBtn) {
      secondaryCtaBtn.addEventListener('click', (e) => {
        e.preventDefault();
        // If product has exactly 1 available variant and atcBtn is ready, use native add to cart
        if (productData && productData.variants && productData.variants.length === 1 && atcBtn && !atcBtn.disabled) {
          atcBtn.click();
          return;
        }

        // Otherwise smooth scroll to buy box to let user pick their size/color variant
        const heroSection = root.querySelector('#pr-hero');
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (heroSection) {
          heroSection.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
          setTimeout(() => {
            const firstVariantPill = root.querySelector('.pr-variant-option .pr-pill-btn:not([disabled])');
            if (firstVariantPill) {
              firstVariantPill.focus();
            } else if (atcBtn) {
              atcBtn.focus();
            }
          }, prefersReducedMotion ? 50 : 500);
        }
      });
    }

    const finalCtaBtn = root.querySelector('.pr-final-cta-btn');
    if (finalCtaBtn) {
      finalCtaBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const heroSection = root.querySelector('#pr-hero');
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (heroSection) {
          heroSection.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
          setTimeout(() => {
            if (atcBtn) atcBtn.focus();
          }, prefersReducedMotion ? 50 : 500);
        }
      });
    }

    // --- 5. Accessible FAQ Accordions ---
    const faqItems = root.querySelectorAll('.pr-faq-item');
    faqItems.forEach((item) => {
      const summary = item.querySelector('.pr-faq-summary');
      if (summary) {
        summary.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            item.open = !item.open;
          }
        });
      }
    });

    // Initial state run
    updateVariantState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPawRinseLanding);
  } else {
    initPawRinseLanding();
  }

  // Support Shopify Theme Editor re-render events
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector('.pawrins-landing')) {
      initPawRinseLanding();
    }
  });
})();
